import type { AdminEpisodeDetail, StoryGraphIssue } from "@storyline/shared";
import { Background, Controls, type Edge, MiniMap, ReactFlow, useEdgesState, useNodesState } from "@xyflow/react";
import { useEffect, useMemo } from "react";
import EndingNode from "@/src/stories/components/graph/ending-node";
import StoryNode from "@/src/stories/components/graph/story-node";
import { buildEpisodeFlow, type StoryFlowNode } from "@/src/stories/utils/graph-view";
import "@xyflow/react/dist/style.css";

const nodeTypes = { story: StoryNode, ending: EndingNode };

type EpisodeGraphCanvasProps = {
	episode: AdminEpisodeDetail;
	issues: StoryGraphIssue[];
};

const EpisodeGraphCanvas: React.FC<EpisodeGraphCanvasProps> = ({ episode, issues }) => {
	const flow = useMemo(() => buildEpisodeFlow(episode, issues), [episode, issues]);
	const [nodes, setNodes, onNodesChange] = useNodesState<StoryFlowNode>([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

	useEffect(() => {
		setNodes(flow.nodes);
		setEdges(flow.edges);
	}, [flow, setNodes, setEdges]);

	return (
		<div className="h-[560px] overflow-hidden rounded-xl border border-border-soft bg-surface">
			<ReactFlow
				nodes={nodes}
				edges={edges}
				nodeTypes={nodeTypes}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				colorMode="dark"
				fitView
				nodesConnectable={false}
				nodesDraggable
				deleteKeyCode={null}
				minZoom={0.2}
			>
				<Background gap={24} />
				<Controls showInteractive={false} />
				<MiniMap pannable zoomable />
			</ReactFlow>
		</div>
	);
};

export default EpisodeGraphCanvas;
